import { useMemo, useEffect, useState } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  MenuItem,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material'
import shadows from '@mui/material/styles/shadows'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format } from 'date-fns'
import { bffService } from '../../../../../../services/utils/axios'
import ErrorFeedback from '../../../../../../components/DataTable/Feedback'

/**
 * ConsumerDeliveryDetails Form Component
 *
 * This component renders a form for consumers to specify their energy preferences and delivery details.
 * It handles form validation, postal code verification, and data submission.
 * The form adapts to the user's contract type and collects appropriate information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.payload - The existing user data to pre-populate the form
 * @param {Function} props.setPayload - Function to update the parent component's payload state
 * @param {Function} props.confirmPayload - Function called when form is successfully submitted
 * @param {Function} props.handleClose - Function to close the form dialog
 * @param {number} props.submit - Trigger for form submission (incremented to trigger)
 * @param {Function} props.setSubmit - Function to reset the submit counter
 * @param {Function} props.setCanNext - Function to enable/disable the next button based on form validity
 *
 * @example
 * <ConsumerDeliveryDetails
 *   payload={contractData}
 *   setPayload={handleSetPayload}
 *   confirmPayload={handleNext}
 *   handleClose={onClose}
 *   submit={submitValue}
 *   setSubmit={setSubmitValue}
 *   setCanNext={setCanNextValue}
 * />
 *
 * @returns {React.ReactElement} A form for consumer preference details
 */

function ConsumerDeliveryDetails({
  payload,
  setPayload,
  confirmPayload,
  submit,
  setSubmit,
  setCanNext,
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (submit > 0) {
      document.getElementById('subTwo').click()
    }
    setSubmit(0)
  }, [submit, setSubmit])
  const closeFeedback = () => {
    setShowError(false)
  }
  const handlePostalCodeChange = async (e, setFieldValue) => {
    const newPostalCode = e.target.value
    setFieldValue('postalCode', newPostalCode)

    if (newPostalCode.length === 5) {
      try {
        const { data } = await bffService.get('/valid-addresses')
        if (data && Array.isArray(data)) {
          // Filter addresses by postal code
          const matchingAddresses = data.filter(
            (address) => address.postalCode === newPostalCode
          )

          if (matchingAddresses.length > 0) {
            // Use first matching address for city
            const city = matchingAddresses[0].city
            setFieldValue('city', city)
          } else {
            setFieldValue('postalCode', '')
            setFieldValue('city', '')
            setShowError(true)
          }
        } else {
          setFieldValue('postalCode', '')
          setFieldValue('city', '')
          setShowError(true)
        }
      } catch (error) {
        setFieldValue('city', '')
        setFieldValue('postalCode', '')
        setShowError(true)
      }
    }
  }

  const initialValues = {
    energyPreference: payload?.energyPreference || 'NONE',
    name: payload?.plantAddress?.name,
    city: payload?.plantAddress?.city || '',
    postalCode: payload?.plantAddress?.postalCode,
    isPlantAddressSameAsAddress: payload?.isPlantAddressSameAsAddress || false,
    marketLocation: payload?.marketLocation || '',
    meterNumber: payload?.meterNumber || '',
    currentUtilitySupplierName: payload?.currentUtilitySupplierName || '',
    endCustomerNumber: payload?.endCustomerNumber || '',
    isPlantTermsAndConditionsAccepted: false, // Consumers don't need plant terms acceptance
    startOfDeliveryDate: payload?.startOfDeliveryDate || null,
    isStartOfDeliveryNextPossibleDate:
      payload?.isStartOfDeliveryNextPossibleDate || false,
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .label(t('LBLBillingAddressOwner'))
          .required(t('LBLCompulsory')),
        city: Yup.string().required(t('LBLCityReq')).label(t('city')),
        postalCode: Yup.string()
          .required(t('LBLPostalCodeError'))
          .label(t('PostalCode')),
        startOfDeliveryDate: Yup.date()
          .nullable()
          .label(t('LBLStartOfDeliveryDate'))
          .when(
            'isStartOfDeliveryNextPossibleDate',
            ([isStartOfDeliveryNextPossibleDate]) => {
              if (!isStartOfDeliveryNextPossibleDate) {
                return Yup.date().required(t('LBLStartOfDeliveryDateReq'))
              }
            }
          ),
        marketLocation: Yup.string()
          .required(t('LBLmarketLocationReq'))
          .label(t('LBLMarketLocation')),
        meterNumber: Yup.string()
          .required(t('LBLmeterNumberReq'))
          .label(t('LBLMeterNumber')),
        energyPreference: Yup.string()
          .required(t('LBLplantTypeReq'))
          .label(t('LBLPlantName')),
        isStartOfDeliveryNextPossibleDate: Yup.bool().label(
          t('LBLNextPossibleDate')
        ),
        currentUtilitySupplierName: Yup.string()
          .required(t('LBLCurrentSupplierNameReq'))
          .label(t('LBLCurrentSupplierName')),
        endCustomerNumber: Yup.string()
          .required(t('LBLLastCustomerNumReq'))
          .label(t('LBLLastCustomerNum')),
      }),
    [t]
  )

  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      energyPreference: values.energyPreference || 'NONE',
      plantAddress: {
        name: values.name,
        city: values.city,
        postalCode: values.postalCode,
      },
      marketLocation: values.marketLocation,
      meterNumber: values.meterNumber,
      isPlantTermsAndConditionsAccepted: false, // Consumers don't need plant terms acceptance
      startOfDeliveryDate: values.startOfDeliveryDate
        ? format(new Date(values.startOfDeliveryDate), 'yyyy-MM-dd')
        : null,
      isStartOfDeliveryNextPossibleDate:
        values.isStartOfDeliveryNextPossibleDate,
      isPlantAddressSameAsAddress: values.isPlantAddressSameAsAddress || false,
      currentUtilitySupplierName: values.currentUtilitySupplierName,
      endCustomerNumber: values.endCustomerNumber,
    }

    setPayload(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
        validateOnChange={true}
      >
        {({ errors, touched, values, setFieldValue, isValid }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}

              <Stack
                mt={5}
                mb={values.isStartOfDeliveryNextPossibleDate ? 5 : 0}
                direction="row"
                spacing={8}
              >
                <MuiTextField
                  select
                  required
                  id="energyPreference"
                  name="energyPreference"
                  open={open}
                  margin="none"
                  label={t('LBLPreference')}
                  onClose={() => setOpen(false)}
                  onOpen={() => setOpen(true)}
                  defaultValue={payload?.energyPreference}
                  value={values.energyPreference}
                  onChange={(event) =>
                    setFieldValue('energyPreference', event.target.value)
                  }
                  sx={{ flex: 1 }}
                >
                  <MenuItem value="HYDRO">{t('LBLHydroPower')}</MenuItem>
                  <MenuItem value="SOLAR">{t('LBLSolarPower')}</MenuItem>
                  <MenuItem value="BIOMASS">{t('LBLBioMass')}</MenuItem>
                  <MenuItem value="WIND">{t('LBLWindEnergy')}</MenuItem>
                  <MenuItem value="NONE">{t('LBLNoPreference')}</MenuItem>
                </MuiTextField>
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {!values.isStartOfDeliveryNextPossibleDate ? (
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="de"
                    >
                      <DatePicker
                        label={t('LBLStartOfDeliveryDate')}
                        PaperProps={{
                          sx: {
                            boxShadow: shadows[3],
                          },
                        }}
                        value={values.startOfDeliveryDate}
                        id={'startOfDeliveryDate'}
                        onChange={(newValue) => {
                          setFieldValue(
                            'startOfDeliveryDate',
                            newValue ? newValue.toDate() : null
                          )
                        }}
                        renderInput={(params) => (
                          <Field
                            component={TextField}
                            {...params}
                            required
                            name="startOfDeliveryDate"
                            margin="none"
                            autoComplete="off"
                            helperText={
                              touched.startOfDeliveryDate &&
                              errors.startOfDeliveryDate
                            }
                            error={
                              errors.startOfDeliveryDate &&
                              touched.startOfDeliveryDate
                            }
                            inputProps={{
                              ...params.inputProps,
                              placeholder: 'TT.MM.JJJJ',
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  ) : undefined}
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isStartOfDeliveryNextPossibleDate"
                        checked={values.isStartOfDeliveryNextPossibleDate}
                        onChange={(event) => {
                          setFieldValue(
                            'isStartOfDeliveryNextPossibleDate',
                            event.target.checked
                          )
                        }}
                      />
                    }
                    label={t('LBLNextPossibleDate')}
                  />
                </div>
              </Stack>

              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="name"
                  name="name"
                  label={t('LBLAddressOwner')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  required
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="postalCode"
                  name="postalCode"
                  label={t('PostalCode')}
                  value={values.postalCode}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  onChange={(e) => handlePostalCodeChange(e, setFieldValue)}
                />
                <Field
                  required
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="city"
                  name="city"
                  label={t('city')}
                  value={values.city}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="marketLocation"
                  name="marketLocation"
                  label={t('LBLMarketLocation')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
                <Field
                  fullWidth
                  required
                  margin="none"
                  component={TextField}
                  id="meterNumber"
                  name="meterNumber"
                  label={t('LBLMeterNumber')}
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="currentUtilitySupplierName"
                  name="currentUtilitySupplierName"
                  label={t('LBLCurrentSupplierName')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>
              <Stack mb={5} direction="row" spacing={8}>
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="endCustomerNumber"
                  name="endCustomerNumber"
                  label={t('LBLLastCustomerNum')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                />
              </Stack>

              <Button
                sx={{ display: 'none' }}
                id="subTwo"
                type="submit"
              ></Button>
            </>
          </Form>
        )}
      </Formik>
      <ErrorFeedback
        open={showError}
        title={t('LBLErrorTitle')}
        severity="warning"
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('LBLAuwAreaError')}
          </Typography>
        }
        handleClose={closeFeedback}
      />
    </>
  )
}
ConsumerDeliveryDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayload: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}
export default ConsumerDeliveryDetails
